import { useSyncExternalStore } from "react";

const authListeners = new Set();
const notifyAuthChange = () => authListeners.forEach(listener => listener());
const subscribeAuth = (listener) => {
  authListeners.add(listener);
  window.addEventListener("storage", listener);
  return () => {
    authListeners.delete(listener);
    window.removeEventListener("storage", listener);
  };
};

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

export const authStore = {
  token: () => localStorage.getItem("cloudkia_admin_token"),
  user: () => parseAuthUser(localStorage.getItem("cloudkia_admin_user")),
  save: (token, user) => {
    localStorage.setItem("cloudkia_admin_token", token);
    localStorage.setItem("cloudkia_admin_user", JSON.stringify(user));
    notifyAuthChange();
  },
  clear: () => {
    localStorage.removeItem("cloudkia_admin_token");
    localStorage.removeItem("cloudkia_admin_user");
    notifyAuthChange();
  },
};

export const useAuthToken = () => useSyncExternalStore(subscribeAuth, authStore.token);
const authUserRaw = () => localStorage.getItem("cloudkia_admin_user") || "{}";
const parseAuthUser = raw => {
  try {
    const user = JSON.parse(raw || "{}");
    return user && typeof user === "object" && !Array.isArray(user) ? user : {};
  } catch {
    return {};
  }
};
export const useAuthUser = () => parseAuthUser(useSyncExternalStore(subscribeAuth, authUserRaw));

let userRefresh = null;
export const refreshAuthUser = () => {
  const token = authStore.token();
  if (!token) return Promise.resolve();
  if (!userRefresh) userRefresh = request("/auth/me").then(user => {
    if (authStore.token() === token) authStore.save(token, user);
  }).finally(() => { userRefresh = null; });
  return userRefresh;
};

const activeRequests = new Set();
const interruptedRequests = new WeakSet();
let activeAnalysis = null;
export const isAbortError = error => error?.name === "AbortError";
export const isAnalysisRunning = () => activeAnalysis !== null;

async function analysisRequest(path, options = {}) {
  activeAnalysis?.controller.abort();
  const controller = new AbortController();
  const externalSignal = options.signal;
  if (externalSignal?.aborted) controller.abort();
  const abort = () => controller.abort();
  externalSignal?.addEventListener("abort", abort, { once: true });
  let release;
  const done = new Promise(resolve => { release = resolve; });
  const analysis = { controller, done };
  activeAnalysis = analysis;
  activeRequests.forEach(requestController => {
    interruptedRequests.add(requestController);
    requestController.abort();
  });
  try {
    return await request(path, { ...options, signal: controller.signal, analysis: true });
  } finally {
    externalSignal?.removeEventListener("abort", abort);
    if (activeAnalysis === analysis) activeAnalysis = null;
    release();
  }
}

async function request(path, options = {}) {
  if (!API_BASE) {
    throw new Error("API URL is not configured. Set REACT_APP_API_BASE_URL and rebuild the portal.");
  }
  if (!options.analysis) {
    if (options.polling && activeAnalysis) throw new DOMException("Polling paused during AI analysis", "AbortError");
    while (activeAnalysis) {
      await activeAnalysis.done;
      if (options.signal?.aborted) throw new DOMException("Request canceled", "AbortError");
    }
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (options.signal?.aborted) controller.abort();
  options.signal?.addEventListener("abort", abort, { once: true });
  if (!options.analysis) activeRequests.add(controller);
  const headers = { Accept: "application/json", ...(options.headers || {}) };
  const token = authStore.token();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body) headers["Content-Type"] = "application/json";

  try {
  const fetchOptions = { ...options };
  delete fetchOptions.analysis;
  delete fetchOptions.polling;
  const response = await fetch(`${API_BASE}${path}`, { ...fetchOptions, signal: controller.signal, headers });
  const payload = await response.json().catch(() => ({}));

  if (response.status === 401) {
    authStore.clear();
    if (!window.location.pathname.endsWith("/login"))
      window.location.replace("/login");
  }
  if (!response.ok) {
    const validationError = payload.errors
      ? Object.values(payload.errors).flat()[0]
      : null;
    throw new Error(
      validationError || payload.message || "Unable to complete the request.",
    );
  }
  return payload.data;
  } catch (error) {
    if (isAbortError(error) && interruptedRequests.has(controller) && !options.polling && !options.signal?.aborted && (!options.method || options.method === "GET")) {
      return request(path, options);
    }
    throw error;
  } finally {
    activeRequests.delete(controller);
    options.signal?.removeEventListener("abort", abort);
  }
}

export const adminApi = {
  login: (email, password) =>
    request("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request("/auth/logout", { method: "POST" }),
  dashboard: (site) => request(`/sites/${site}/dashboard`),
  saveVisitorNote: (site, session, data) => request(`/sites/${site}/dashboard/visitors/${session}`, { method: "PUT", body: JSON.stringify(data) }),
  agentAvailability: (site) => request(`/sites/${site}/availability`),
  setAgentAvailability: (site, availability) => request(`/sites/${site}/availability`, { method: "PUT", body: JSON.stringify({ availability }) }),
  availabilityHeartbeat: (site, signal) => request(`/sites/${site}/availability/heartbeat`, { method: "POST", signal, polling: true }),
  tickets: (site, status = "", signal, polling = false) =>
    request(
      `/sites/${site}/tickets?perPage=50${status ? `&status=${status}` : ""}`,
      { signal, polling },
    ),
  ticket: (site, id) => request(`/sites/${site}/tickets/${id}`),
  viewTicket: (site, id) => request(`/sites/${site}/tickets/${id}/view`, { method: "POST" }),
  readTicket: (site, id, messageId) => request(`/sites/${site}/tickets/${id}/read`, { method: "POST", body: JSON.stringify({ messageId }) }),
  releaseTicket: (site, id) => request(`/sites/${site}/tickets/${id}/release`, { method: "POST" }),
  acceptTicket: (site, id) => request(`/sites/${site}/tickets/${id}/accept`, { method: "POST" }),
  ticketTyping: (site, id) => request(`/sites/${site}/tickets/${id}/typing`, { method: "POST" }),
  replyToTicket: (site, id, content) => request(`/sites/${site}/tickets/${id}/messages`, { method: "POST", body: JSON.stringify({ content }) }),
  emailTicket: (site, id, content) => request(`/sites/${site}/tickets/${id}/email`, { method: "POST", body: JSON.stringify({ content }) }),
  closeTicket: (site, id) => request(`/sites/${site}/tickets/${id}/close`, { method: "POST" }),
  conversations: (site, filters = {}) => {
    const query = new URLSearchParams({ perPage: "50" });
    Object.entries(typeof filters === "string" ? { status: filters } : filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    return request(`/sites/${site}/conversations?${query}`);
  },
  conversation: (site, id) => request(`/sites/${site}/conversations/${id}`),
  readConversation: (site, id, messageId) => request(`/sites/${site}/conversations/${id}/read`, { method: "POST", body: JSON.stringify({ messageId }) }),
  viewConversation: (site, id) => request(`/sites/${site}/conversations/${id}/view`, { method: "POST" }),
  analytics: (site, days = 30, signal, funnel = "search") => request(`/sites/${site}/analytics?days=${days}&funnel=${encodeURIComponent(funnel)}`, { signal, cache: "no-store" }),
  liveVisitors: (site, signal) => request(`/sites/${site}/analytics/live-visitors`, { signal, cache: "no-store", polling: true }),
  saveAnalyticsFunnel: (site, funnel) => request(`/sites/${site}/analytics/funnel`, { method: "PUT", body: JSON.stringify(funnel) }),
  analyticsTrends: (site, days, signal, page = "", trafficRating = "all") => {
    const query = new URLSearchParams({ days: String(days) });
    if (page) query.set("page", page);
    query.set("trafficRating", trafficRating);
    return request(`/sites/${site}/analytics/trends?${query}`, { signal, cache: "no-store" });
  },
  analyticsAiProviders: (site, signal) => request(`/sites/${site}/analytics/ai-providers`, { signal }),
  analyzeSite: (site, days, signal, trafficRating = "all", provider = { key: "local" }, focusKeyword = "") => analysisRequest(`/sites/${site}/analytics/ai-analysis`, { method: "POST", body: JSON.stringify({ days, trafficRating, providerKey: provider.key, model: provider.model, focusKeyword }), signal }),
  websiteKeywords: (site, days, trafficRating, signal) => request(`/sites/${site}/analytics/website-keywords`, { method: "POST", body: JSON.stringify({ days, trafficRating }), signal, cache: "no-store" }),
  pageKeywords: (site, offset = 0, keyword = "", signal) => {
    const query = new URLSearchParams({ offset: String(offset) });
    if (keyword) query.set("keyword", keyword);
    return request(`/sites/${site}/analytics/page-keywords?${query}`, { signal, cache: "no-store" });
  },
  analyzeEngagement: (site, filters, signal) => analysisRequest(`/sites/${site}/behaviour/ai-analysis`, { method: "POST", body: JSON.stringify(filters), signal }),
  resetVisitorActivity: (site) => request(`/sites/${site}/analytics/activity`, { method: "DELETE" }),
  behaviour: (site, filters = {}, signal) => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query.set(key, value);
    });
    return request(`/sites/${site}/behaviour?${query}`, { cache: "no-store", signal });
  },
  settings: (site, signal) => request(`/sites/${site}/settings`, { signal }),
  users: (site) => request(`/sites/${site}/users`),
  createUser: (site, data) => request(`/sites/${site}/users`, { method: "POST", body: JSON.stringify(data) }),
  updateUser: (site, id, data) => request(`/sites/${site}/users/${encodeURIComponent(id)}`, { method: "PUT", body: JSON.stringify(data) }),
  saveSettings: (site, data) =>
    request(`/sites/${site}/settings`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  aiSettings: (site) => request(`/sites/${site}/ai-settings`),
  saveAiSettings: (site, data) =>
    request(`/sites/${site}/ai-settings`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
  saveAiProvider: (site, providerKey, data) =>
    request(`/sites/${site}/ai-providers/${providerKey}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
};
